/**
 * Developer-only demo seeder UI (synthetic demo data).
 *
 * Mounted at /admin/demo-seeder behind <InternalAdminRoute>. Additionally
 * gated by VITE_ENABLE_DEMO_SEEDER so it can be disabled per environment.
 *
 * No nav entry — URL access only.
 *
 * Shop scope:
 * The seeder always targets the *active* Supabase project that the app is
 * connected to. On mount we ask the edge function for the project's full
 * shop list and let the user pick one. We never assume a UUID — the chosen
 * shopId is passed on every list/seed/cleanup call. Cleanup additionally
 * requires both shopId and runId so it cannot drift to a sibling shop.
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Sparkles, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ENABLED = import.meta.env.VITE_ENABLE_DEMO_SEEDER === "true";

interface RunRow {
  runId: string;
  total: number;
  completed: number;
  active: number;
}

interface ShopOption {
  id: string;
  name: string | null;
}

/**
 * supabase-js wraps non-2xx edge function responses as FunctionsHttpError
 * with the raw Response on `.context`. Read it once to surface our
 * structured { step, message, details } body in the toast + last-result.
 */
async function readErrorBody(error: unknown): Promise<any | null> {
  try {
    const ctx = (error as { context?: Response })?.context;
    if (ctx && typeof ctx.json === "function") {
      return await ctx.clone().json();
    }
  } catch { /* fall through */ }
  return null;
}

export default function DemoSeeder() {
  const { toast } = useToast();
  const [shops, setShops] = useState<ShopOption[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopId, setSelectedShopId] = useState<string>("");
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<unknown>(null);

  async function loadShops() {
    setShopsLoading(true);
    const { data, error } = await supabase.functions.invoke("demo-seed", {
      body: { action: "shops" },
    });
    setShopsLoading(false);
    if (error) {
      const body = await readErrorBody(error);
      toast({
        title: "Failed to load shops",
        description: body?.message ?? error.message,
        variant: "destructive",
      });
      return;
    }
    const list = ((data as { shops?: ShopOption[] })?.shops ?? []);
    setShops(list);
    // Pre-select the shop that matches the default OptiBayHQ name, when unambiguous.
    if (list.length > 0 && !selectedShopId) {
      const obayMatches = list.filter((s) => (s.name ?? "").toLowerCase().includes("optibay"));
      if (obayMatches.length === 1) setSelectedShopId(obayMatches[0].id);
      else if (list.length === 1) setSelectedShopId(list[0].id);
    }
  }

  async function refresh(shopIdOverride?: string) {
    const shopId = shopIdOverride ?? selectedShopId;
    if (!shopId) {
      setRuns([]);
      return;
    }
    const { data, error } = await supabase.functions.invoke("demo-seed", {
      body: { action: "list", shopId },
    });
    if (error) {
      const body = await readErrorBody(error);
      toast({
        title: "Failed to list runs",
        description: body?.message ?? error.message,
        variant: "destructive",
      });
      setRuns([]);
      return;
    }
    setRuns((data as { runs?: RunRow[] })?.runs ?? []);
  }

  useEffect(() => { if (ENABLED) loadShops(); }, []);
  useEffect(() => { if (ENABLED && selectedShopId) refresh(selectedShopId); }, [selectedShopId]);

  async function handleSeed() {
    if (!selectedShopId) {
      toast({ title: "Pick a shop first", variant: "destructive" });
      return;
    }
    setBusy("seed");
    setLastResult(null);
    const { data, error } = await supabase.functions.invoke("demo-seed", {
      body: { action: "seed", shopId: selectedShopId },
    });
    setBusy(null);
    if (error) {
      const body = await readErrorBody(error);
      setLastResult(body ?? { error: error.message });
      // Multi-shop match (409) should not happen in this flow because we pass
      // a concrete shopId, but surface it cleanly if it does.
      if (body?.needsSelection && Array.isArray(body.matches)) {
        toast({
          title: "Multiple shops matched",
          description: "Pick one from the dropdown and retry.",
          variant: "destructive",
        });
        setShops(body.matches);
        return;
      }
      toast({
        title: body?.step ? `Seed failed at ${body.step}` : "Seed failed",
        description: body?.message ?? error.message,
        variant: "destructive",
      });
      return;
    }
    setLastResult(data);
    toast({ title: "Demo data seeded", description: `Run ${(data as any)?.result?.runId ?? ""}` });
    refresh();
  }

  async function handleCleanup(runId: string) {
    if (!selectedShopId) {
      toast({ title: "Pick a shop first", variant: "destructive" });
      return;
    }
    setBusy(`cleanup:${runId}`);
    setLastResult(null);
    const { data, error } = await supabase.functions.invoke("demo-seed", {
      body: { action: "cleanup", shopId: selectedShopId, runId },
    });
    setBusy(null);
    if (error) {
      const body = await readErrorBody(error);
      setLastResult(body ?? { error: error.message });
      toast({
        title: body?.step ? `Cleanup failed at ${body.step}` : "Cleanup failed",
        description: body?.message ?? error.message,
        variant: "destructive",
      });
      return;
    }
    const result = (data as any)?.result;
    if (result?.aborted) {
      toast({ title: "Cleanup aborted", description: result.reason, variant: "destructive" });
    } else {
      toast({ title: "Demo data removed", description: `Run ${runId}` });
    }
    setLastResult(data);
    refresh();
  }

  if (!ENABLED) {
    return (
      <div className="p-8 max-w-2xl">
        <h1 className="text-2xl font-bold mb-2">Demo Seeder disabled</h1>
        <p className="text-muted-foreground">
          Set <code>VITE_ENABLE_DEMO_SEEDER=true</code> in your <code>.env.local</code> and reload.
        </p>
      </div>
    );
  }

  const selectedShop = shops.find((s) => s.id === selectedShopId) ?? null;

  return (
    <div className="p-8 max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Demo Data Seeder</h1>
        <p className="text-muted-foreground text-sm">
          Developer-only. Creates marked simulated tickets in the selected shop on the active
          Supabase project. All rows tagged{" "}
          <code>source=demo_seed</code> +{" "}
          <code>created_by=demo_seed:&lt;runId&gt;</code>.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Target shop</CardTitle>
          <CardDescription>
            Shops are loaded from the Supabase project this app is connected to. Pick the shop
            the demo data should be written to.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Select
            value={selectedShopId}
            onValueChange={(v) => setSelectedShopId(v)}
            disabled={shopsLoading || shops.length === 0}
          >
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder={shopsLoading ? "Loading shops…" : "Select a shop"} />
            </SelectTrigger>
            <SelectContent>
              {shops.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name ?? "(unnamed)"} — <span className="font-mono text-xs">{s.id.slice(0, 8)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedShop && (
            <p className="text-xs text-muted-foreground font-mono">{selectedShop.id}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Seed new run</CardTitle>
          <CardDescription>
            ~20 completed tickets across the last 21 days, plus ~10 active Dayboard tickets for today.
            Uses real technicians, bays, advisors, jobs catalog, and (when available) passport
            proficiency/efficiency for the selected shop.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={busy !== null || !selectedShopId}>
                {busy === "seed" ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Seeding…</>
                ) : (
                  <><Sparkles className="mr-2 h-4 w-4" />Seed Demo Data</>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Seed demo data?</AlertDialogTitle>
                <AlertDialogDescription>
                  Inserts ~30 demo tickets (plus job/timer/business-state/advisor rows) into{" "}
                  <strong>{selectedShop?.name ?? "the selected shop"}</strong>. Every row is marked
                  and can be cleaned up by run id.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleSeed}>Seed</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing demo runs</CardTitle>
          <CardDescription>
            {selectedShopId ? "Counts shown before cleanup. Scoped to the selected shop." : "Pick a shop to see existing demo runs."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!selectedShopId ? (
            <p className="text-sm text-muted-foreground">No shop selected.</p>
          ) : runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No demo runs.</p>
          ) : (
            <div className="space-y-2">
              {runs.map((r) => (
                <div key={r.runId} className="flex items-center justify-between border rounded-md p-3">
                  <div>
                    <div className="font-mono text-sm">{r.runId}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.total} tickets · {r.completed} completed · {r.active} active
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" disabled={busy !== null}>
                        {busy === `cleanup:${r.runId}` ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cleaning…</>
                        ) : (
                          <><Trash2 className="mr-2 h-4 w-4" />Clean</>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Clean run {r.runId}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Deletes {r.total} tickets and all their child rows from{" "}
                          <strong>{selectedShop?.name ?? "the selected shop"}</strong>. Cleanup
                          re-verifies the demo marker on every ticket before deleting and aborts
                          on mismatch.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleCleanup(r.runId)}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          Clean
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {lastResult !== null && (
        <Card>
          <CardHeader><CardTitle>Last result</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-96">
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
